/**
 * DashboardLayout — persistent shell with collapsible sidebar.
 *
 * Nielsen Norman #3 (User control): collapse/expand saved to localStorage.
 * Nielsen Norman #4 (Consistency): icons always visible; labels shown when expanded.
 * Nielsen Norman #6 (Recognition over recall): tooltip labels on collapsed icons.
 * Apple HIG: 200 ms ease-out transition, icon-only mode, pill toggle at edge.
 */
import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Upload, Settings, LogOut, FileText,
  Sun, Moon, Menu, X, ChevronRight, ChevronLeft, Shield,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const BASE_NAV = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/upload',    label: 'Upload',    icon: Upload },
  { path: '/settings',  label: 'Settings',  icon: Settings },
];

// ── SidebarBody — MODULE LEVEL ────────────────────────────────────────────────
// MUST be defined outside DashboardLayout. If defined inside, React creates a
// brand-new function reference on every DashboardLayout render (sidebar toggle,
// theme change, etc.) and treats it as a different component type — causing a
// full unmount+remount of the sidebar DOM tree on each state change.
// Module-level = stable function reference = React does a prop update only.
function SidebarBody({ slim, nav, location, dark, onToggleTheme, onSignOut, onCloseMobile, userEmail }) {
  return (
    <div className="flex flex-col h-full">

      {/* Brand */}
      <div className={`flex items-center border-b border-slate-200 dark:border-slate-700
        ${slim ? 'justify-center px-4 py-5' : 'gap-3 px-6 py-5'}`}>
        <div className="w-8 h-8 rounded-xl bg-sky-600 flex items-center justify-center flex-shrink-0 shadow-sm">
          <FileText className="w-4 h-4 text-white" />
        </div>
        {!slim && (
          <div>
            <p className="font-bold text-sm text-slate-900 dark:text-slate-100 leading-tight">CA Statement</p>
            <p className="text-[11px] text-slate-400 dark:text-slate-500 leading-tight">Engine</p>
          </div>
        )}
      </div>

      {/* Nav links */}
      <nav className={`flex-1 py-4 space-y-0.5 ${slim ? 'px-2' : 'px-3'}`}>
        {nav.map(({ path, label, icon: Icon }) => {
          const active = location.pathname === path ||
            (path !== '/dashboard' && location.pathname.startsWith(path));
          return (
            <Link
              key={path}
              to={path}
              onClick={onCloseMobile}
              title={slim ? label : undefined}
              className={`
                flex items-center rounded-lg text-sm font-medium transition-all duration-150
                ${slim
                  ? 'justify-center w-10 h-10 mx-auto'
                  : 'gap-3 px-3 py-2.5 w-full'}
                ${active
                  ? 'bg-sky-50 dark:bg-sky-900/30 text-sky-700 dark:text-sky-400 shadow-sm'
                  : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-slate-200'}
              `}
            >
              <Icon className="w-4 h-4 flex-shrink-0" />
              {!slim && <span className="flex-1">{label}</span>}
              {!slim && active && <ChevronRight className="w-3 h-3 opacity-60" />}
            </Link>
          );
        })}
      </nav>

      {/* Footer controls */}
      <div className={`border-t border-slate-200 dark:border-slate-700 py-3 space-y-0.5 ${slim ? 'px-2' : 'px-3'}`}>
        <button
          onClick={onToggleTheme}
          title={slim ? (dark ? 'Light Mode' : 'Dark Mode') : undefined}
          className={`
            flex items-center rounded-lg text-sm text-slate-600 dark:text-slate-400
            hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-slate-200
            transition-all duration-150
            ${slim ? 'justify-center w-10 h-10 mx-auto' : 'w-full gap-3 px-3 py-2.5'}
          `}
        >
          {dark
            ? <Sun className="w-4 h-4 flex-shrink-0" />
            : <Moon className="w-4 h-4 flex-shrink-0" />}
          {!slim && (dark ? 'Light Mode' : 'Dark Mode')}
        </button>

        <button
          onClick={onSignOut}
          title={slim ? 'Sign Out' : undefined}
          className={`
            flex items-center rounded-lg text-sm text-red-600 dark:text-red-400
            hover:bg-red-50 dark:hover:bg-red-900/20
            transition-all duration-150
            ${slim ? 'justify-center w-10 h-10 mx-auto' : 'w-full gap-3 px-3 py-2.5'}
          `}
        >
          <LogOut className="w-4 h-4 flex-shrink-0" />
          {!slim && 'Sign Out'}
        </button>

        {!slim && userEmail && (
          <div className="px-3 pt-1 pb-0.5">
            <p className="text-[11px] text-slate-400 truncate">{userEmail}</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default function DashboardLayout({ children }) {
  const { user, signOut, isAdmin } = useAuth();

  const nav = isAdmin
    ? [...BASE_NAV, { path: '/admin', label: 'Admin Panel', icon: Shield }]
    : BASE_NAV;

  const location = useLocation();
  const navigate = useNavigate();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [dark, setDark] = useState(() =>
    document.documentElement.classList.contains('dark')
  );
  const [collapsed, setCollapsed] = useState(() =>
    localStorage.getItem('sidebar-collapsed') === 'true'
  );

  const toggleTheme = () => {
    const next = !dark;
    setDark(next);
    document.documentElement.classList.toggle('dark', next);
    localStorage.setItem('theme', next ? 'dark' : 'light');
  };

  const toggleCollapse = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem('sidebar-collapsed', String(next));
  };

  const handleSignOut = async () => {
    await signOut();
    toast.success('Signed out');
    navigate('/login');
  };

  const closeMobile = () => setMobileOpen(false);

  // Shared props for both sidebar instances (desktop + mobile overlay)
  const sidebarProps = {
    nav,
    location,
    dark,
    onToggleTheme: toggleTheme,
    onSignOut:     handleSignOut,
    onCloseMobile: closeMobile,
    userEmail:     user?.email,
  };

  // ── Layout ────────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex overflow-x-hidden">

      {/* ── Desktop sidebar ─────────────────────────────────────────────────── */}
      <aside
        className={`
          hidden lg:flex flex-col
          bg-white dark:bg-slate-900
          border-r border-slate-200 dark:border-slate-700
          fixed inset-y-0 left-0 z-30
          transition-all duration-200 ease-out
          ${collapsed ? 'w-16' : 'w-64'}
        `}
      >
        <SidebarBody slim={collapsed} {...sidebarProps} />

        {/* Collapse toggle — pill on the right edge */}
        <button
          onClick={toggleCollapse}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className="
            absolute -right-3 top-1/2 -translate-y-1/2
            w-6 h-6 rounded-full
            bg-white dark:bg-slate-900
            border border-slate-200 dark:border-slate-700
            shadow-sm hover:shadow-md
            flex items-center justify-center
            text-slate-400 hover:text-slate-700 dark:hover:text-slate-200
            hover:scale-110 active:scale-95
            transition-all duration-150
            z-10
          "
        >
          {collapsed
            ? <ChevronRight className="w-3 h-3" />
            : <ChevronLeft  className="w-3 h-3" />}
        </button>
      </aside>

      {/* ── Mobile sidebar overlay ──────────────────────────────────────────── */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={closeMobile}
          />
          <aside className="absolute left-0 top-0 bottom-0 w-72 bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-700 flex flex-col">
            <div className="flex justify-end p-3">
              <button
                onClick={closeMobile}
                className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              <SidebarBody slim={false} {...sidebarProps} />
            </div>
          </aside>
        </div>
      )}

      {/* ── Main content ────────────────────────────────────────────────────── */}
      <div
        className={`
          flex-1 flex flex-col min-h-screen min-w-0 overflow-x-hidden
          transition-all duration-200 ease-out
          ${collapsed ? 'lg:ml-16' : 'lg:ml-64'}
        `}
      >
        {/* Mobile topbar */}
        <header className="lg:hidden flex items-center justify-between px-4 py-3 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 sticky top-0 z-20">
          <button
            onClick={() => setMobileOpen(true)}
            className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-lg bg-sky-600 flex items-center justify-center">
              <FileText className="w-3 h-3 text-white" />
            </div>
            <span className="font-semibold text-sm text-slate-900 dark:text-slate-100">CA Statement</span>
          </div>
          <div className="w-9" />
        </header>

        <main className="flex-1 p-4 lg:p-6 min-w-0 overflow-x-hidden">
          {children}
        </main>
      </div>
    </div>
  );
}
